/**
 * Typed event emitter for engine events.
 *
 * Generic over EngineEventMap so every `on('eventName', handler)` call
 * is fully type-checked at compile time.
 *
 * @see docs/ims-engine-api/02-api-contract.md — Event-Katalog
 */

import type { EngineEventHandler, EngineEventMap } from './types';

type ListenerSet = Set<EngineEventHandler<never>>;

export class EventEmitter {
  private _listeners = new Map<keyof EngineEventMap, ListenerSet>();
  private _disposed = false;

  /**
   * Subscribe to an event. Returns an unsubscribe function.
   */
  on<K extends keyof EngineEventMap>(
    event: K,
    handler: EngineEventHandler<K>,
  ): () => void {
    if (this._disposed) return () => {};

    let set = this._listeners.get(event);
    if (!set) {
      set = new Set();
      this._listeners.set(event, set);
    }
    set.add(handler as EngineEventHandler<never>);

    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from an event.
   */
  off<K extends keyof EngineEventMap>(
    event: K,
    handler: EngineEventHandler<K>,
  ): void {
    const set = this._listeners.get(event);
    if (set) {
      set.delete(handler as EngineEventHandler<never>);
      if (set.size === 0) this._listeners.delete(event);
    }
  }

  /**
   * Emit an event to all registered listeners.
   *
   * For events with `void` payload, call `emit('sceneLoaded')`.
   * For events with a payload, call `emit('error', { code, message })`.
   */
  emit<K extends keyof EngineEventMap>(
    ...args: EngineEventMap[K] extends void
      ? [event: K]
      : [event: K, payload: EngineEventMap[K]]
  ): void {
    if (this._disposed) return;

    const [event, payload] = args;
    const set = this._listeners.get(event);
    if (!set) return;

    for (const handler of set) {
      try {
        (handler as (payload?: unknown) => void)(payload);
      } catch (err) {
        console.error('[ims-engine] Error in event handler:', event, err);
      }
    }
  }

  /**
   * Remove all listeners. Called during engine dispose.
   * After dispose, on/emit are no-ops.
   */
  dispose(): void {
    this._listeners.clear();
    this._disposed = true;
  }

  /** Number of registered listeners across all events. */
  get listenerCount(): number {
    let count = 0;
    for (const set of this._listeners.values()) {
      count += set.size;
    }
    return count;
  }
}
